import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes

export const SWUpdateNotification: React.FC = () => {
  const { t } = useTranslation();
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [showUpdate, setShowUpdate] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const checkForWaiting = (registration: ServiceWorkerRegistration) => {
      if (registration.waiting) {
        setWaitingWorker(registration.waiting);
        setShowUpdate(true);
      }
    };

    // Listen for new service workers
    const handleRegistration = (registration: ServiceWorkerRegistration) => {
      checkForWaiting(registration);

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(newWorker);
            setShowUpdate(true);
          }
        });
      });
    };

    navigator.serviceWorker.ready.then(handleRegistration);

    // Listen for SW_ACTIVATED message to reload
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SW_ACTIVATED') {
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);

    // Periodically check for updates
    const interval = setInterval(() => {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
      });
    }, UPDATE_CHECK_INTERVAL_MS);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
      clearInterval(interval);
    };
  }, []);

  const handleUpdate = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    setShowUpdate(false);
  }, [waitingWorker]);

  return (
    <Snackbar
      open={showUpdate}
      message={t('pwa.updateAvailable')}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      action={
        <Button color="primary" size="small" onClick={handleUpdate}>
          {t('pwa.updateNow')}
        </Button>
      }
    />
  );
};
